import React, { useEffect, useState } from 'react';

import { FormProvider, useFormContext } from 'react-hook-form';

import { useReservationForm } from '../../shared/hooks/use-reservation-form';
import { BusinessLocation, ReservationConfirmation } from '../../shared/types';
import { FORM_STEP } from '../types/step-config.types';

import { ContactInfoStep } from './steps/contact-info-step';
import { ReservationActionStep } from './steps/reservation-action-step';
import { ReservationConfirmationStep } from './steps/reservation-confirmation-step';
import { TimeslotStep } from './steps/timeslot-step';
import { WidgetHeader } from './widget-header';

interface WidgetReservationStepsProps {
  reservationConfirmation: ReservationConfirmation | null;
  businessLocation: BusinessLocation;
  currentStep: FORM_STEP;
  onFormStepChange: (step: FORM_STEP) => void;
  onReservationComplete: (response: ReservationConfirmation) => void;
}

const STEPS_WITH_BACK = [FORM_STEP.CONTACT_INFO, FORM_STEP.RESERVATION_SUMMARY];

const PREVIOUS_STEP: Partial<Record<FORM_STEP, FORM_STEP>> = {
  [FORM_STEP.CONTACT_INFO]: FORM_STEP.TIMESLOT,
  [FORM_STEP.RESERVATION_SUMMARY]: FORM_STEP.CONTACT_INFO,
};

function WidgetReservationSteps({
  reservationConfirmation,
  businessLocation,
  currentStep,
  onFormStepChange,
  onReservationComplete,
}: WidgetReservationStepsProps) {
  const { clearErrors } = useFormContext();

  const handleTryAgain = () => {
    clearErrors();
    onFormStepChange(FORM_STEP.TIMESLOT);
  };

  return (
    <div className="flex flex-col relative z-[1] py-1">
      {currentStep === FORM_STEP.TIMESLOT && (
        <TimeslotStep
          businessLocation={businessLocation}
          onNext={() => onFormStepChange(FORM_STEP.CONTACT_INFO)}
        />
      )}

      {currentStep === FORM_STEP.CONTACT_INFO && (
        <ContactInfoStep
          businessLocation={businessLocation}
          onNext={() => onFormStepChange(FORM_STEP.RESERVATION_SUMMARY)}
        />
      )}

      {currentStep === FORM_STEP.RESERVATION_SUMMARY && (
        <ReservationActionStep
          businessLocation={businessLocation}
          onSuccess={onReservationComplete}
        />
      )}

      {currentStep === FORM_STEP.RESERVATION_CONFIRMATION && reservationConfirmation && (
        <ReservationConfirmationStep
          businessLocation={businessLocation}
          reservationConfirmation={reservationConfirmation}
          onTryAgain={handleTryAgain}
        />
      )}
    </div>
  );
}

interface WidgetReservationContentProps {
  businessLocation: BusinessLocation;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}

export function WidgetReservationContent({
  businessLocation,
  isOpen,
  onOpen,
  onClose,
}: WidgetReservationContentProps) {
  const [reservationConfirmation, setReservationConfirmation] =
    useState<ReservationConfirmation | null>(null);

  const methods = useReservationForm(businessLocation, 'onTouched');
  const { reset } = methods;

  const [currentStep, setCurrentStep] = useState<FORM_STEP>(FORM_STEP.TIMESLOT);

  const showBack = isOpen && STEPS_WITH_BACK.includes(currentStep);

  const goBack = () => {
    const prev = PREVIOUS_STEP[currentStep];

    if (!prev) return;

    methods.clearErrors();
    setCurrentStep(prev);
  };

  const onReservationComplete = (response: ReservationConfirmation) => {
    setReservationConfirmation(response);
    setCurrentStep(FORM_STEP.RESERVATION_CONFIRMATION);
  };

  useEffect(() => {
    if (isOpen) return;

    const shouldResetForm = currentStep === FORM_STEP.RESERVATION_CONFIRMATION;

    if (!shouldResetForm) return;

    setReservationConfirmation(null);
    setCurrentStep(FORM_STEP.TIMESLOT);
    reset();
  }, [isOpen, currentStep, reset]);

  return (
    <FormProvider {...methods}>
      <WidgetHeader
        isOpen={isOpen}
        showBack={showBack}
        onBack={goBack}
        onOpen={onOpen}
        onClose={onClose}
      />
      {isOpen && (
        <WidgetReservationSteps
          businessLocation={businessLocation}
          currentStep={currentStep}
          onFormStepChange={setCurrentStep}
          reservationConfirmation={reservationConfirmation}
          onReservationComplete={onReservationComplete}
        />
      )}
    </FormProvider>
  );
}
